import { Fragment, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@avds/icons";
import { Status } from "@/components/ui/Status";
import { useSession } from "@/lib/auth";
import { fmtDate, fmtDateRange, fmtDayTime, fmtMoney } from "@/lib/format";
import {
  addOrderNote,
  deleteOrderNote,
  listOrderNotes,
  type OpenOrder,
  type OpenOrderList,
} from "./openOrders.api";

export interface OpenOrdersSearch {
  q?: string;
  lead?: string; // "mine"
  state?: string; // an O-code
}

/** The O-codes an open order can carry, in process order. */
export const OPEN_STATES = ["O50", "O51", "O52", "O54", "O60"] as const;

const COLS = 10;

// Five free-text columns: at `.truncate`'s own 10rem minimum each they alone
// outgrow a laptop screen, so this table lets them narrow further.
const NARROW = { minWidth: "6rem" } as const;

interface OpenOrdersClientProps {
  result: OpenOrderList | undefined;
  isLoading: boolean;
  search: OpenOrdersSearch;
  updateParams: (updates: Record<string, string>) => void;
}

/** "2026-05" of a YYYY-MM-DD, or "" when the order has no date. */
const monthOf = (day: string | null): string => (day ? day.slice(0, 7) : "");

function matches(o: OpenOrder, q: string): boolean {
  const needle = q.toLowerCase();
  return [
    o.talisOrderNumber,
    o.customerName,
    o.customerCode,
    o.projectLeadName,
    o.talisRemarks,
    o.latestNote?.body,
    ...o.activities.map((a) => a.invoiceText),
  ].some((v) => v?.toLowerCase().includes(needle));
}

/** "O50 ×2" chips: the order's activities by O-code, in process order. */
function stateCounts(o: OpenOrder): [string, number][] {
  const counts = new Map<string, number>();
  for (const a of o.activities) {
    if (a.status) counts.set(a.status, (counts.get(a.status) ?? 0) + 1);
  }
  return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
}

// The administration's "Openstaande orders" Excel, live: every TALIS order that
// was executed but not yet invoiced (O50–O60), grouped per month of its last
// activity. A row opens to the activities and the internal notes.
export function OpenOrdersClient({ result, isLoading, search, updateParams }: OpenOrdersClientProps) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const me = session.data?.email.toLowerCase() ?? null;
  const [open, setOpen] = useState<string | null>(null);

  const orders = useMemo(() => {
    const all = result?.orders ?? [];
    return all.filter(
      (o) =>
        (!search.q || matches(o, search.q)) &&
        (search.lead !== "mine" || (me !== null && o.projectLeadEmail === me)) &&
        (!search.state || o.activities.some((a) => a.status === search.state)),
    );
  }, [result, search.q, search.lead, search.state, me]);

  const months = useMemo(() => {
    const byMonth = new Map<string, OpenOrder[]>();
    for (const o of orders) {
      const key = monthOf(o.lastActivityDate);
      byMonth.set(key, [...(byMonth.get(key) ?? []), o]);
    }
    return [...byMonth.entries()].sort(([a], [b]) => b.localeCompare(a));
  }, [orders]);

  const monthName = (key: string) =>
    key
      ? new Intl.DateTimeFormat(i18n.language, { month: "long", year: "numeric" }).format(new Date(`${key}-15T12:00:00Z`))
      : t("openOrders.noDate");

  const totalOpen = orders.reduce((sum, o) => sum + o.openRevenueCents, 0);

  return (
    <div className="avds-page" data-screen-label="Back-office Open orders">
      <div style={{ marginBottom: "var(--space-4)" }}>
        <h1 className="t-h2">{t("openOrders.title")}</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "var(--fs-md)", marginTop: "2px" }}>
          {t("openOrders.subtitle")}
          {result?.syncedAt && <> · {t("openOrders.syncedAt", { time: fmtDayTime(result.syncedAt) })}</>}
        </p>
      </div>

      <div
        className="avds-card"
        style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottom: "none" }}
      >
        <div
          className="avds-card__body"
          style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", padding: "var(--space-3) var(--space-4)" }}
        >
          <div className="avds-input-group" style={{ width: "280px" }}>
            <span className="avds-input-group__icon">
              <Icon name="search" size={15} />
            </span>
            <input
              placeholder={t("openOrders.search")}
              value={search.q ?? ""}
              onChange={(e) => updateParams({ q: e.target.value })}
            />
          </div>
          <select
            className="avds-select"
            style={{ width: "220px" }}
            value={search.state ?? ""}
            onChange={(e) => updateParams({ state: e.target.value })}
            aria-label={t("openOrders.state.label")}
          >
            <option value="">{t("openOrders.state.all")}</option>
            {OPEN_STATES.map((s) => (
              <option key={s} value={s}>
                {s} · {t(`openOrders.state.${s}` as never)}
              </option>
            ))}
          </select>
          <label className="avds-hstack" style={{ fontSize: "var(--fs-sm)", gap: "var(--space-2)" }}>
            <input
              type="checkbox"
              checked={search.lead === "mine"}
              onChange={(e) => updateParams({ lead: e.target.checked ? "mine" : "" })}
            />
            {t("openOrders.mine")}
          </label>
          <div className="avds-spacer" />
          <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-secondary)" }}>
            {t("openOrders.totalOpen")} <span className="mono">{fmtMoney(totalOpen)}</span>
          </span>
        </div>
      </div>

      <div className="avds-table-wrap" style={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
        <table className="avds-table">
          <thead>
            <tr>
              <th style={{ width: "72px" }}>{t("openOrders.col.order")}</th>
              <th className="truncate" style={{ ...NARROW, width: "11%" }}>{t("openOrders.col.customer")}</th>
              <th className="truncate" style={{ ...NARROW, width: "9%" }}>{t("openOrders.col.lead")}</th>
              <th className="truncate" style={{ ...NARROW, width: "10%" }}>{t("openOrders.col.talisRemarks")}</th>
              <th className="truncate" style={{ ...NARROW, width: "10%" }}>{t("openOrders.col.invoiceText")}</th>
              <th className="truncate" style={{ ...NARROW, width: "12%" }}>{t("openOrders.col.notes")}</th>
              <th>{t("openOrders.col.period")}</th>
              <th className="num">{t("openOrders.col.revenue")}</th>
              <th className="num">{t("openOrders.col.open")}</th>
              <th>{t("openOrders.col.state")}</th>
            </tr>
          </thead>
          <tbody>
            {months.map(([key, rows]) => (
              <Fragment key={key || "none"}>
                <tr>
                  <td colSpan={COLS} style={{ background: "var(--surface-sunken)" }}>
                    <span className="primary" style={{ textTransform: "capitalize" }}>{monthName(key)}</span>{" "}
                    <span style={{ color: "var(--text-tertiary)", fontSize: "var(--fs-sm)" }}>
                      · {t("openOrders.monthCount", { count: rows.length })} ·{" "}
                      <span className="mono">{fmtMoney(rows.reduce((s, o) => s + o.openRevenueCents, 0))}</span>
                    </span>
                  </td>
                </tr>
                {rows.map((o) => (
                  <OrderRow
                    key={o.talisOrderNumber}
                    order={o}
                    isOpen={open === o.talisOrderNumber}
                    onToggle={() => setOpen(open === o.talisOrderNumber ? null : o.talisOrderNumber)}
                  />
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
        {!isLoading && orders.length === 0 && (
          <div style={{ padding: "var(--space-12)", textAlign: "center" }}>
            <div className="t-h3" style={{ color: "var(--text-primary)" }}>
              {t("openOrders.empty.title")}
            </div>
            <p style={{ color: "var(--text-secondary)", fontSize: "var(--fs-sm)", marginTop: "var(--space-2)" }}>
              {t("openOrders.empty.desc")}
            </p>
          </div>
        )}
      </div>

      <div style={{ marginTop: "var(--space-3)", fontSize: "var(--fs-sm)", color: "var(--text-tertiary)" }}>
        {t("openOrders.count", { shown: orders.length, total: result?.orders.length ?? 0 })}
      </div>
    </div>
  );
}

function OrderRow({ order: o, isOpen, onToggle }: { order: OpenOrder; isOpen: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const invoiceTexts = o.activities.map((a) => a.invoiceText).filter((s): s is string => !!s);

  return (
    <>
      <tr onClick={onToggle} style={{ cursor: "pointer" }} aria-expanded={isOpen}>
        <td className="mono">
          <span className="avds-hstack" style={{ gap: "var(--space-1)" }}>
            <Icon name={isOpen ? "chevron-down" : "chevron-right"} size={14} />
            {o.talisOrderNumber}
          </span>
        </td>
        <td className="truncate" style={NARROW} title={o.customerName ?? undefined}>
          <span className="primary">{o.customerName ?? "—"}</span>
        </td>
        <td className="truncate" style={NARROW} title={o.projectLeadName ?? undefined}>{o.projectLeadName ?? "—"}</td>
        <td className="truncate" style={NARROW} title={o.talisRemarks ?? undefined}>{o.talisRemarks ?? ""}</td>
        <td className="truncate" style={NARROW} title={invoiceTexts.join("\n")}>{invoiceTexts[0] ?? ""}</td>
        <td className="truncate" style={NARROW} title={o.latestNote?.body}>
          {o.latestNote && (
            <>
              {o.noteCount > 1 && <span className="avds-chip" style={{ marginRight: "var(--space-1)" }}>{o.noteCount}</span>}
              {o.latestNote.body}
            </>
          )}
        </td>
        <td className="mono">{fmtDateRange(o.firstActivityDate, o.lastActivityDate)}</td>
        <td className="mono num">{fmtMoney(o.revenueCents)}</td>
        <td className="mono num">{fmtMoney(o.openRevenueCents)}</td>
        <td>
          <span className="avds-hstack" style={{ gap: "var(--space-1)", flexWrap: "wrap" }}>
            {stateCounts(o).map(([code, n]) => (
              <span key={code} className="avds-chip mono" title={t(`openOrders.state.${code}` as never, { defaultValue: code })}>
                {code}
                {n > 1 && ` ×${n}`}
              </span>
            ))}
          </span>
        </td>
      </tr>
      {isOpen && (
        <tr>
          <td colSpan={COLS} style={{ background: "var(--surface-sunken)", whiteSpace: "normal" }}>
            <OrderDetail order={o} />
          </td>
        </tr>
      )}
    </>
  );
}

function OrderDetail({ order: o }: { order: OpenOrder }) {
  const { t } = useTranslation();

  return (
    <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: "var(--space-6)", padding: "var(--space-3) var(--space-2)" }}>
      <div>
        {o.talisRemarks && (
          <div style={{ marginBottom: "var(--space-4)" }}>
            <div className="t-eyebrow">{t("openOrders.col.talisRemarks")}</div>
            <p style={{ whiteSpace: "pre-wrap", marginTop: "var(--space-1)" }}>{o.talisRemarks}</p>
          </div>
        )}
        <div className="t-eyebrow" style={{ marginBottom: "var(--space-2)" }}>{t("openOrders.activities")}</div>
        <table className="avds-table">
          <thead>
            <tr>
              <th>{t("openOrders.act.id")}</th>
              <th>{t("openOrders.act.date")}</th>
              <th>{t("openOrders.col.state")}</th>
              <th>{t("openOrders.col.invoiceText")}</th>
              <th className="num">{t("openOrders.col.revenue")}</th>
              <th className="num">{t("openOrders.act.invoiced")}</th>
            </tr>
          </thead>
          <tbody>
            {o.activities.map((a) => (
              <tr key={a.talisActivityId}>
                <td className="mono">{a.talisActivityId}</td>
                <td className="mono">{fmtDate(a.date)}</td>
                <td>
                  {a.status && (
                    <span className="avds-chip mono" title={t(`openOrders.state.${a.status}` as never, { defaultValue: a.status })}>
                      {a.status}
                    </span>
                  )}
                </td>
                <td style={{ whiteSpace: "pre-wrap" }}>{a.invoiceText ?? ""}</td>
                <td className="mono num">{fmtMoney(a.revenueCents)}</td>
                <td className="mono num">{fmtMoney(a.invoicedCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {o.proformas.length > 0 && (
          <div className="avds-hstack" style={{ marginTop: "var(--space-3)", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <span className="t-eyebrow">{t("openOrders.proformas")}</span>
            {o.proformas.map((p) => (
              <Link key={p.id} to="/proformas/$id" params={{ id: p.id }} className="avds-hstack mono" style={{ gap: "var(--space-2)" }}>
                {p.number} <Status status={p.status} />
              </Link>
            ))}
          </div>
        )}
      </div>
      <OrderNotes orderNumber={o.talisOrderNumber} />
    </div>
  );
}

function OrderNotes({ orderNumber }: { orderNumber: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const notes = useQuery({ queryKey: ["order-notes", orderNumber], queryFn: () => listOrderNotes(orderNumber) });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["order-notes", orderNumber] });
    void queryClient.invalidateQueries({ queryKey: ["open-orders"] });
  };
  const add = useMutation({
    mutationFn: () => addOrderNote(orderNumber, draft.trim()),
    onSuccess: () => {
      setDraft("");
      refresh();
    },
  });
  const remove = useMutation({ mutationFn: deleteOrderNote, onSuccess: refresh });

  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: "var(--space-2)" }}>{t("openOrders.col.notes")}</div>
      <textarea
        className="avds-textarea"
        style={{ width: "100%" }}
        placeholder={t("openOrders.note.placeholder")}
        value={draft}
        maxLength={4000}
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="avds-hstack" style={{ justifyContent: "flex-end", marginTop: "var(--space-2)" }}>
        <button
          type="button"
          className="avds-btn avds-btn--primary avds-btn--sm"
          disabled={!draft.trim() || add.isPending}
          onClick={() => add.mutate()}
        >
          {t("openOrders.note.add")}
        </button>
      </div>
      {(add.isError || remove.isError) && (
        <div className="avds-alert avds-alert--danger" style={{ marginTop: "var(--space-2)" }}>
          <Icon name="alert-circle" size={16} />
          <div>{t("openOrders.note.error")}</div>
        </div>
      )}
      <ul style={{ listStyle: "none", padding: 0, margin: "var(--space-3) 0 0", display: "grid", gap: "var(--space-3)" }}>
        {(notes.data ?? []).map((n) => (
          <li key={n.id} style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: "var(--space-2)" }}>
            <div className="avds-hstack" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)", color: "var(--text-tertiary)" }}>
              <span>
                {n.authorName} · <span className="mono">{fmtDayTime(n.createdAt)}</span>
              </span>
              {n.mine && (
                <button
                  type="button"
                  className="avds-btn avds-btn--ghost avds-btn--icon avds-btn--sm"
                  aria-label={t("openOrders.note.delete")}
                  title={t("openOrders.note.delete")}
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(n.id)}
                >
                  <Icon name="trash" size={14} />
                </button>
              )}
            </div>
            <p style={{ whiteSpace: "pre-wrap", marginTop: "var(--space-1)" }}>{n.body}</p>
          </li>
        ))}
        {notes.data?.length === 0 && (
          <li style={{ color: "var(--text-tertiary)", fontSize: "var(--fs-sm)" }}>{t("openOrders.note.none")}</li>
        )}
      </ul>
    </div>
  );
}
